import type { ReadonlyURLSearchParams } from "$app/state";
import { m } from "./paraglide/messages.js";
import type { AnalyticsGate } from "@colossus/contracts";
import type { Message } from "./locale.ts";
import { TIME_PARAMS } from "./scope.ts";
import type { ScopeFallback, ScopeParam } from "./scope.ts";

type ViewGroup = "growth" | "engagement" | "audience" | "lens" | "leaderboards";

export type SubjectFocus = null | "channel" | "channel-pair" | "member" | "member-pair";

type ViewParent = "invites" | "content" | "member";

type MemberLens = "members/detail" | "members/leveling";

export interface ViewEntry {
  readonly id: string;
  readonly group: ViewGroup;
  readonly title: Message;
  readonly requires: SubjectFocus;
  readonly gates: readonly AnalyticsGate[];
  readonly params: readonly ScopeParam[];
  readonly fallback?: ScopeFallback;
  readonly memberLens?: MemberLens;
  readonly leveling?: boolean;
  readonly parent?: ViewParent;
  readonly searchWords?: Message;
}

const FILTERS = ["rolesInclude", "rolesExclude", "channelsInclude", "channelsExclude"] as const;
const ROLES = ["rolesInclude", "rolesExclude"] as const;
const CHANNELS = ["channelsInclude", "channelsExclude"] as const;

const VIEW_INDEX = {
  growth: {
    group: "growth",
    title: m.view_growth_title,
    requires: null,
    gates: ["growth"],
    params: [...TIME_PARAMS, ...ROLES],
    searchWords: m.view_growth_search_words,
  },
  engagement: {
    group: "engagement",
    title: m.view_engagement_title,
    requires: null,
    gates: ["engagement"],
    params: [...TIME_PARAMS, ...FILTERS],
    searchWords: m.view_engagement_search_words,
  },
  audience: {
    group: "audience",
    title: m.view_audience_title,
    requires: null,
    gates: ["audience"],
    searchWords: m.view_audience_search_words,
    params: [...TIME_PARAMS, ...FILTERS],
  },
  "members/detail": {
    group: "lens",
    title: m.view_member_activity_title,
    requires: "member",
    gates: ["engagement", "profiles"],
    params: [...TIME_PARAMS, ...CHANNELS, "user", "pairs"],
    parent: "member",
  },
  "members/leveling": {
    group: "lens",
    title: m.view_member_leveling_title,
    requires: "member",
    gates: ["profiles"],
    params: [...TIME_PARAMS, "user"],
    leveling: true,
    parent: "member",
  },
  "engagement/channels/detail": {
    group: "lens",
    title: m.view_channel_detail_title,
    requires: "channel",
    gates: ["engagement"],
    params: [...TIME_PARAMS, ...ROLES, "channel"],
  },
  "members/compare": {
    group: "lens",
    title: m.view_members_compare_title,
    requires: "member-pair",
    gates: ["engagement", "profiles"],
    params: [...TIME_PARAMS, ...CHANNELS, "user", "userB"],
  },
  "engagement/channels/compare": {
    group: "lens",
    title: m.view_channels_compare_title,
    requires: "channel-pair",
    gates: ["engagement"],
    params: [...TIME_PARAMS, ...ROLES, "channel", "channelB"],
  },
  members: {
    group: "leaderboards",
    title: m.view_members_title,
    requires: null,
    gates: ["engagement", "profiles"],
    params: [...TIME_PARAMS, ...FILTERS, "metric"],
  },
  "engagement/channels": {
    group: "leaderboards",
    title: m.view_engagement_channels_title,
    requires: null,
    gates: ["engagement"],
    params: [...TIME_PARAMS, ...FILTERS],
  },
  "growth/invites/inviters": {
    group: "leaderboards",
    title: m.panel_inviters_title,
    requires: null,
    gates: ["growth", "profiles"],
    params: [...TIME_PARAMS, ...ROLES],
    parent: "invites",
  },
  "growth/invites/links": {
    group: "leaderboards",
    title: m.panel_invite_codes_title,
    requires: null,
    gates: ["growth", "profiles"],
    params: [...TIME_PARAMS, ...ROLES],
    parent: "invites",
  },
  "content/emoji": {
    group: "leaderboards",
    title: m.panel_emoji_board_title,
    requires: null,
    gates: ["engagement"],
    params: [...TIME_PARAMS, ...ROLES],
    parent: "content",
  },
  "content/commands": {
    group: "leaderboards",
    title: m.panel_command_board_title,
    requires: null,
    gates: ["engagement"],
    params: [...TIME_PARAMS, ...FILTERS],
    parent: "content",
  },
  "audience/apps": {
    group: "leaderboards",
    title: m.panel_app_board_title,
    requires: null,
    gates: ["audience"],
    params: [...TIME_PARAMS, ...ROLES],
  },
  events: {
    group: "leaderboards",
    title: m.view_events_title,
    requires: null,
    gates: ["engagement"],
    params: [...TIME_PARAMS],
  },
  levels: {
    group: "leaderboards",
    title: m.view_levels_title,
    requires: null,
    gates: ["profiles"],
    params: [...TIME_PARAMS],
    fallback: "all",
    memberLens: "members/leveling",
    leveling: true,
  },
} as const satisfies Record<string, Omit<ViewEntry, "id">>;

export type ViewId = keyof typeof VIEW_INDEX;

export const viewEntry = (id: ViewId): ViewEntry => ({ id, ...VIEW_INDEX[id] });

export const isViewId = (id: string): id is ViewId => Object.hasOwn(VIEW_INDEX, id);

const VIEW_ENTRIES: readonly ViewEntry[] = (Object.keys(VIEW_INDEX) as ViewId[]).map(viewEntry);

export const viewParams = (viewId: string): ReadonlySet<ScopeParam> => {
  const entry = VIEW_ENTRIES.find((candidate) => candidate.id === viewId);
  return new Set(entry === undefined ? TIME_PARAMS : entry.params);
};

export const memberLensOf = (viewId: string): MemberLens => {
  const entry = VIEW_ENTRIES.find((candidate) => candidate.id === viewId);
  return entry === undefined || entry.memberLens === undefined
    ? "members/detail"
    : entry.memberLens;
};

export const onlyParams = (
  params: ReadonlyURLSearchParams,
  honoured: Iterable<string>,
): URLSearchParams => {
  const kept = new Set(honoured);
  return new URLSearchParams([...params].filter(([name]) => kept.has(name)));
};
